// novem atom — form field composition (NovemHR-ekranlar port).
//
// The B3/B4 modal forms repeated the same shape at every field: a
// <label htmlFor> + a control (Input/Select/Textarea) + an optional hint
// or inline error, with the id/aria wiring done by hand each time. This
// promotes that shape so the label↔control association + the
// aria-describedby chain are wired once, correctly.
//
// Composition model: FormField owns a generated id and injects it onto
// its single control child by cloning — the child receives `id`,
// `aria-describedby` (hint + error ids, joined), `aria-invalid` (when
// error), and `aria-required` (when required). The control components in
// this library (Input, Select, Textarea) all forward these props onto
// their native element, so the wiring is transparent.
//
// Accessibility:
//   * <label for={id}> — clicking the label focuses the control.
//   * Required marker is visual (·) AND announced via aria-required on
//     the control (not just the asterisk, which SRs may skip).
//   * Error renders role="alert" so it announces on appearance; the
//     control points at it via aria-describedby.
//   * Hint and error are mutually visible (error does not replace hint) —
//     both ids join into aria-describedby when both are present.

import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
} from "react";

import { cx } from "../lib/cx.ts";
import { HelpHint } from "../HelpHint/HelpHint.tsx";

export interface FormFieldProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /** Visible label text. */
  readonly label: ReactNode;
  /** The single control element (Input/Select/Textarea/...). */
  readonly children: ReactElement;
  /** Helper text below the control. */
  readonly hint?: ReactNode;
  /** Inline error text. When set, the control is marked invalid + described by it. */
  readonly error?: ReactNode;
  /** Marks the field required (visual indicator + aria-required on control). */
  readonly required?: boolean;
  /**
   * Explanation behind a "?" beside the label — for a field whose name cannot
   * carry its own meaning. Distinct from `hint`: a hint is always-visible text
   * the reader needs while typing, help is on-demand text they need once.
   * Requires `helpLabel` (the trigger's accessible name, already localized).
   */
  readonly help?: ReactNode;
  /** Localized accessible name of the help trigger, e.g. "Anahtar hakkında yardım". */
  readonly helpLabel?: string;
}

interface InjectableControlProps {
  readonly id?: string | undefined;
  readonly "aria-describedby"?: string | undefined;
  readonly "aria-invalid"?: boolean | undefined;
  readonly "aria-required"?: boolean | undefined;
}

export const FormField = forwardRef<HTMLDivElement, FormFieldProps>(
  (
    {
      label,
      children,
      hint,
      error,
      required = false,
      help,
      helpLabel,
      className,
      ...rest
    },
    ref,
  ) => {
    const id = useId();
    const controlId = `${id}-control`;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const hasError = error !== undefined && error !== null && error !== false;

    // aria-describedby chain — hint first, then error, defined-only.
    const describedBy =
      [hint !== undefined ? hintId : null, hasError ? errorId : null]
        .filter((s): s is string => s !== null)
        .join(" ") || undefined;

    // Inject id + aria onto the single control child. The control already
    // carries an id/aria-describedby from the consumer? We override with
    // ours so the <label for> association is guaranteed.
    const child = Children.only(children);
    const control = isValidElement<InjectableControlProps>(child)
      ? cloneElement(child, {
          id: controlId,
          "aria-describedby": describedBy,
          "aria-invalid": hasError ? true : undefined,
          "aria-required": required ? true : undefined,
        })
      : child;

    return (
      <div ref={ref} data-component="form-field" className={cx("nai-form-field", className)} {...rest}>
        <div className="nai-form-field-labelrow">
          <label htmlFor={controlId} className="nai-form-field-label">
            {label}
            {required && (
              // Decorative — the "required" semantic is carried by
              // aria-required on the control (SRs announce it); the glyph
              // must NOT leak into the field's accessible name.
              <span className="nai-form-field-required" aria-hidden="true">
                {" "}
                ·
              </span>
            )}
          </label>
          {/* helpLabel gates the render rather than defaulting: an unlabelled
              icon button is a screen-reader dead end, and a default label here
              could only be English. */}
          {help !== undefined && helpLabel !== undefined && (
            <HelpHint label={helpLabel}>
              {help}
            </HelpHint>
          )}
        </div>
        {control}
        {hint !== undefined && (
          <p id={hintId} className="nai-form-field-hint">
            {hint}
          </p>
        )}
        {hasError && (
          <p id={errorId} role="alert" className="nai-form-field-error">
            {error}
          </p>
        )}
      </div>
    );
  },
);
FormField.displayName = "FormField";
